import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookmarkPlus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Feedback";
import { SelectField, TextField } from "@/components/ui/Field";
import { meApi } from "@/lib/tickets-api";

/** Named list filters of the signed-in user: pick one to apply it, save the current filters under a name. */
export function SavedFilters({ current, onApply }: { current: string; onApply: (query: string) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const saved = useQuery({ queryKey: ["me", "saved-filters"], queryFn: meApi.savedFilters });
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["me", "saved-filters"] });
  const save = useMutation({
    mutationFn: () => meApi.saveFilter(name.trim(), current),
    onSuccess: async () => {
      await refresh();
      setNaming(false);
      setName("");
    },
  });
  const remove = useMutation({ mutationFn: meApi.deleteFilter, onSuccess: refresh });
  const match = saved.data?.find((f) => f.query === current);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) save.mutate();
  };

  return (
    <div className="flex flex-wrap items-end gap-2 border-b border-line px-5 py-3">
      <SelectField
        label={t("tickets.saved.label")}
        value={match?.id ?? ""}
        onChange={(e) => {
          const chosen = saved.data?.find((f) => f.id === e.target.value);
          if (chosen) onApply(chosen.query);
        }}
        className="min-w-56"
      >
        <option value="">{saved.data?.length ? t("tickets.saved.choose") : t("tickets.saved.none")}</option>
        {saved.data?.map((filter) => (
          <option key={filter.id} value={filter.id}>
            {filter.name}
          </option>
        ))}
      </SelectField>
      {match ? (
        <Button variant="ghost" onClick={() => remove.mutate(match.id)} busy={remove.isPending}>
          <Trash2 className="size-4" aria-hidden="true" />
          {t("tickets.saved.delete")}
        </Button>
      ) : (
        current && (
          <Button variant="secondary" onClick={() => setNaming(true)}>
            <BookmarkPlus className="size-4" aria-hidden="true" />
            {t("tickets.saved.save")}
          </Button>
        )
      )}
      <Dialog
        open={naming}
        onOpenChange={setNaming}
        title={t("tickets.saved.dialogTitle")}
        description={t("tickets.saved.dialogBody")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setNaming(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="save-filter" busy={save.isPending} disabled={!name.trim()}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        <form id="save-filter" onSubmit={onSubmit} className="space-y-3">
          {save.isError && <Alert>{t("common.errorGeneric")}</Alert>}
          <TextField
            label={t("tickets.saved.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            required
            autoFocus
          />
        </form>
      </Dialog>
    </div>
  );
}
